import { yupResolver } from "@hookform/resolvers/yup";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Banner,
  Button,
  Card,
  Page,
  Stack,
  Text,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React, { useEffect } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { Colors, Spacings, View } from "react-native-ui-lib";
import { CountProductModalRootParamList } from "@/types";
import {
  default as Product,
  default as ProductModel,
} from "../../../../utilities/data/model/Product";
import {
  useCountProductModalRootContext,
  useCurrentCountContext,
} from "../../../../utilities/stock-count/hooks";
import { useStockCounter } from "../StockCountScreen/hooks";
import { CountItemValidationSchema } from "./constants";
import { CountItemFormValues } from "./types";
import { useTranslation } from "../../../../utilities/i18n";
import { StockSelectionType } from "../../../../utilities/stock-count";
import { useQueryFindBaseProductByBarcodeAsync } from "../../../../utilities/retailer-data-core";
import { DataCoreBaseProduct } from "@towersystems/roam-common/lib/generated-types";
import { CoreProductDetailsCard } from "./CoreProductDetailsCard";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";

export type CountNonProductModalScreenProps = StackScreenProps<
  CountProductModalRootParamList,
  "CountNonProductModalScreen"
> & {
  matchingBaseProduct?: DataCoreBaseProduct;
};

const CountNonProductModal = ({
  navigation,
  matchingBaseProduct,
  ...props
}: CountNonProductModalScreenProps) => {
  const { t } = useTranslation("stock_counts");

  const database = useDatabase();
  const { stockCount } = useCountProductModalRootContext();

  const { updateStockSelection, updateSelectedStockCountLine } =
    useCurrentCountContext();

  const { countStockItem } = useStockCounter();

  const { handleSubmit, clearErrors, watch, setError, formState } =
    useFormContext<CountItemFormValues>();

  const [existingProduct, setExistingProduct] = React.useState<
    Product | undefined
  >(undefined);

  const { showMessage } = useFlashMessages();

  const { route } = props;
  const { barcode } = route.params;

  const watchedStockNumber = watch("barcode");

  React.useEffect(() => {
    clearErrors();
    database
      .get<ProductModel>("products")
      .query(Q.where("stock_number", Q.eq(watchedStockNumber)))
      .then((products) => {
        if (products.length) {
          setExistingProduct(products[0]);
          setError("barcode", {
            type: "manual",
            message: t("product:error.barcode_exists"),
          });
        }
      });
  }, [watchedStockNumber, setError]);

  const { isLoading, isSubmitting, isValid, isSubmitSuccessful } = formState;

  const handleOnSubmit = React.useCallback(
    async (fieldValues: CountItemFormValues) => {
      if (!stockCount.id) return;
      // Locally set the expected value, this will be updated when the stock count is synced
      // to the server and the stock count line is updated with the correct expected value
      let expected = 0;
      if (matchingBaseProduct) {
        const location = await stockCount.location.fetch();
        const matchingLocationPricing =
          matchingBaseProduct.inventoryPricing.find(
            (pricing) =>
              pricing.locationId.toString() == location.retailerLocationKey,
          ) ?? matchingBaseProduct.inventoryPricing[0];

        expected = matchingLocationPricing?.stockOnHand || 0;
      }

      countStockItem(
        stockCount.id,
        { barcode: fieldValues.barcode },
        fieldValues.counted,
        {
          name: fieldValues.name,
          expected: expected,
        },
      )
        .then((result) => {
          /*
          Make sure that when the window closes the newly created stock item
          is selected and in focus
          */
          if (!result) {
            navigation.getParent()?.goBack();
            return;
          }
          const { stockCountItem, stockCountLine } = result;

          if (stockCountItem) {
            updateStockSelection({
              stockIdentifier: {
                productId: stockCountLine.product.id ?? undefined,
                barcode: stockCountLine.barcode || undefined,
              },
              triggerUpdatedAt: false,
              selectionType: StockSelectionType.MANUAL_ENTRY,
            });
            updateSelectedStockCountLine(stockCountLine);
          }

          navigation.getParent()?.goBack();
        })
        .catch((e) => {
          console.log(e);
          showMessage({
            severity: "error",
            message: "Failed to add non-product",
          });
        });
    },
    [navigation, stockCount.id, countStockItem, showMessage],
  );

  const matchingProductFoundMarkup = existingProduct ? (
    <Banner
      title={t("common:banner.matching_product_found.title")}
      status="warning"
      onPress={async () => {
        navigation.reset({
          index: 0,
          routes: [{ name: "CountProductModalScreen" }],
        });
      }}
    >
      <Text>{t("common:banner.matching_product_found.description")}</Text>
    </Banner>
  ) : undefined;

  const nonMatchingMarkup = (
    <Banner
      status="warning"
      title={t("common:banner.no_matching_product_found.title")}
      onPress={async () => {
        navigation.navigate(
          "CreateProductRootScreen",
          //@ts-ignore
          {
            defaultValues: {
              stockNumber: watchedStockNumber,
              locationId: stockCount.location.id,
            },
          },
        );
      }}
    >
      <Text>{t("common:banner.no_matching_product_found.description")}</Text>
    </Banner>
  );

  useEffect(() => {
    // if barcode exists focus and select quantity by default
    if (!barcode) return;
  }, [barcode]);

  const disabled =
    isLoading ||
    isSubmitting ||
    !isValid ||
    isSubmitSuccessful ||
    !!existingProduct;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={matchingBaseProduct ? "Add Product" : "Add Non Matched Product"}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const baseProductFoundMarkup = matchingBaseProduct ? (
    <Banner status="info" title={t("banner.base_product_found.title")}>
      <Text>{t("banner.base_product_found.description")}</Text>
    </Banner>
  ) : undefined;

  const banners = baseProductFoundMarkup
    ? baseProductFoundMarkup
    : matchingProductFoundMarkup ?? nonMatchingMarkup;

  const matchingProductDetailsMarkup = matchingBaseProduct ? (
    <CoreProductDetailsCard
      dataCoreBaseProduct={matchingBaseProduct}
      stockCount={stockCount}
    />
  ) : undefined;

  return (
    <Page footer={footerMarkup}>
      <Stack vertical>
        <View>{banners}</View>
        <Card title={t("card.count_details.title")}>
          <Stack spacing={Spacings.s5} vertical>
            <Controller
              name="name"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    label={t("form.name.label")}
                    placeholder={t("form.name.placeholder")}
                    value={field.value}
                    onChangeText={field.onChange}
                    readonly={!!matchingBaseProduct}
                    validationMessage={fieldState.error?.message?.toString()}
                  />
                );
              }}
            />

            <Controller
              name="barcode"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    label={t("form.barcode.label")}
                    placeholder={t("form.barcode.placeholder")}
                    value={field.value}
                    onChangeText={field.onChange}
                    readonly={!!matchingBaseProduct}
                    validationMessage={fieldState.error?.message?.toString()}
                  />
                );
              }}
            />
            <Controller
              name="counted"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    label={t("form.counted.label")}
                    inputMode={"numeric" as const}
                    keyboardType={"numeric" as const}
                    placeholder={t("form.counted.placeholder")}
                    value={field.value}
                    onChangeText={field.onChange}
                    validationMessage={fieldState.error?.message?.toString()}
                  />
                );
              }}
            />
          </Stack>
        </Card>
        {matchingProductDetailsMarkup}
      </Stack>
    </Page>
  );
};

const CountNonProductFormRenderer = ({
  initialFormValues,
  ...props
}: {
  initialFormValues?: Partial<CountItemFormValues>;
} & CountNonProductModalScreenProps) => {
  const methods = useForm<CountItemFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      ...initialFormValues,
      counted: 1,
    },
    resolver: yupResolver(CountItemValidationSchema),
  });

  return (
    <FormProvider {...methods}>
      <CountNonProductModal {...props} />
    </FormProvider>
  );
};

export const CountNonProductModalScreen = (
  props: CountNonProductModalScreenProps,
) => {
  const { route } = props;
  const { barcode } = route.params;

  const [checkedBaseProduct, setCheckedBaseProduct] =
    React.useState<boolean>(false);

  const [baseProduct, setBaseProduct] = React.useState<
    DataCoreBaseProduct | undefined
  >(undefined);

  const { query } = useQueryFindBaseProductByBarcodeAsync();

  React.useEffect(() => {
    if (!barcode) {
      setCheckedBaseProduct(false);
      return;
    }
    query(barcode)
      .then((baseProduct) => {
        setCheckedBaseProduct(true);
        setBaseProduct(baseProduct);
      })
      .catch(() => {
        setCheckedBaseProduct(false);
      })
      .finally(() => {
        setCheckedBaseProduct(true);
      });
  }, []);

  if (!checkedBaseProduct) {
    return (
      <PlaceholderLoadingScreen
        headerShown={false}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <CountNonProductFormRenderer
      matchingBaseProduct={baseProduct}
      initialFormValues={{
        barcode: barcode,
        name: baseProduct ? baseProduct?.stockDescription : undefined,
        counted: 1,
      }}
      {...props}
    />
  );
};
